/**
 * useCampaignsData Hook
 *
 * Manages campaigns data including:
 * - Fetching campaigns with stats
 * - Creating campaigns
 * - Updating campaigns
 * - Deleting campaigns
 */

import { useState, useCallback, useEffect } from "react";
import {
  collection,
  getDocs,
  query,
  orderBy,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  serverTimestamp,
  writeBatch,
} from "firebase/firestore";
import { db } from "../firebase";
import { useToast } from "../components/shared";

export const useCampaignsData = () => {
  const toast = useToast();
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(null);

  // Fetch campaigns
  const fetchCampaigns = useCallback(async () => {
    try {
      setLoading(true);
      const campaignsQuery = query(
        collection(db, "discovery_campaigns"),
        orderBy("createdAt", "desc")
      );
      const campaignsSnapshot = await getDocs(campaignsQuery);
      const campaignsData = campaignsSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      // Fetch all mailshots once (instead of once per campaign)
      const mailshotsQuery = query(
        collection(db, "discovery_mailshots"),
        orderBy("createdAt", "desc")
      );
      const mailshotsSnapshot = await getDocs(mailshotsQuery);
      const allMailshots = mailshotsSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      // Calculate stats for each campaign using the already-fetched mailshots
      const campaignsWithStats = campaignsData.map((campaign) => {
        // Filter mailshots for this campaign
        const campaignMailshots = allMailshots.filter(
          (m) => m.campaignId === campaign.id
        );

        // Calculate stats
        const totalMailshots = campaignMailshots.length;
        const sentMailshots = campaignMailshots.filter(
          (m) => m.mailedOut
        ).length;

        // Get unique properties across all mailshots in campaign
        const allPropertyIds = new Set();
        const sentPropertyIds = new Set();

        campaignMailshots.forEach((mailshot) => {
          mailshot.propertyIds?.forEach((id) => allPropertyIds.add(id));
          if (mailshot.mailedOut) {
            mailshot.propertyIds?.forEach((id) => sentPropertyIds.add(id));
          }
        });

        return {
          ...campaign,
          mailshots: campaignMailshots, // Include mailshots array
          stats: {
            totalMailshots,
            sentMailshots,
            totalProperties: allPropertyIds.size,
            sentProperties: sentPropertyIds.size,
          },
        };
      });

      setCampaigns(campaignsWithStats);
      return campaignsWithStats;
    } catch (error) {
      console.error("Error fetching campaigns:", error);
      toast.error("Failed to load campaigns");
      return [];
    } finally {
      setLoading(false);
    }
  }, [toast]);

  // Create campaign
  const createCampaign = useCallback(
    async (name, description = "") => {
      try {
        const newCampaign = {
          name: name.trim(),
          description: description.trim(),
          createdAt: serverTimestamp(),
          status: "active",
          stats: {
            totalMailshots: 0,
            sentMailshots: 0,
            totalProperties: 0,
            sentProperties: 0,
          },
        };

        const docRef = await addDoc(
          collection(db, "discovery_campaigns"),
          newCampaign
        );
        toast.success("Campaign created successfully");
        await fetchCampaigns();
        return docRef.id;
      } catch (error) {
        console.error("Error creating campaign:", error);
        if (error.code === "permission-denied") {
          toast.error("Permission denied: Only admins can create campaigns.");
        } else {
          toast.error("Failed to create campaign: " + error.message);
        }
        return null;
      }
    },
    [toast, fetchCampaigns]
  );

  // Update campaign
  const updateCampaign = useCallback(
    async (campaignId, updates) => {
      try {
        await updateDoc(doc(db, "discovery_campaigns", campaignId), updates);
        toast.success("Campaign updated successfully");
        await fetchCampaigns();
        return true;
      } catch (error) {
        console.error("Error updating campaign:", error);
        toast.error("Failed to update campaign: " + error.message);
        return false;
      }
    },
    [toast, fetchCampaigns]
  );

  // Delete campaign and all its mailshots
  const deleteCampaign = useCallback(
    async (campaignId) => {
      if (
        !window.confirm(
          "Are you sure you want to delete this campaign? This will also delete all mailshots in this campaign and cannot be undone."
        )
      ) {
        return false;
      }

      try {
        setDeleting(campaignId);

        const batch = writeBatch(db);

        // 1. Delete the campaign
        batch.delete(doc(db, "discovery_campaigns", campaignId));

        // 2. Find and delete all mailshots in this campaign
        const mailshotsSnapshot = await getDocs(
          collection(db, "discovery_mailshots")
        );
        const campaignMailshots = mailshotsSnapshot.docs.filter(
          (doc) => doc.data().campaignId === campaignId
        );

        for (const mailshotDoc of campaignMailshots) {
          const mailshotData = mailshotDoc.data();

          // Delete the mailshot
          batch.delete(doc(db, "discovery_mailshots", mailshotDoc.id));

          // Remove mailshot from properties
          if (mailshotData.propertyIds && mailshotData.propertyIds.length > 0) {
            // Note: We can't use arrayRemove in batch for multiple docs easily
            // This will be handled by a cleanup function or we accept orphaned refs
          }
        }

        await batch.commit();

        // Update local state
        setCampaigns((prev) => prev.filter((c) => c.id !== campaignId));
        toast.success("Campaign and all its mailshots deleted successfully");
        return true;
      } catch (error) {
        console.error("Error deleting campaign:", error);
        if (error.code === "permission-denied") {
          toast.error("Permission denied: Only admins can delete campaigns.");
        } else {
          toast.error("Failed to delete campaign: " + error.message);
        }
        return false;
      } finally {
        setDeleting(null);
      }
    },
    [toast]
  );

  // Initial fetch
  useEffect(() => {
    fetchCampaigns();
  }, [fetchCampaigns]);

  return {
    campaigns,
    loading,
    deleting,
    fetchCampaigns,
    createCampaign,
    updateCampaign,
    deleteCampaign,
  };
};
